import { useEffect, useRef, useState } from "react";
import { Bell, Flame, Zap } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";

const LABEL = { SETUP: "SETUP", TRIGGER: "TRIGGER", CONFIRMED: "CONFIRMED" };

export default function AlertsBell() {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seen = useRef(new Set());
  const firstLoad = useRef(true);

  const poll = async () => {
    try {
      const { data } = await client.get("/alerts", { params: { direction: "long" } });
      if (!firstLoad.current) {
        (data.alerts || []).forEach((a) => {
          if (!seen.current.has(a.id) && !a.read) {
            const up = a.to_state === "CONFIRMED";
            toast[up ? "success" : "message"](`${a.ticker}: ${a.from_state} → ${a.to_state}`, {
              description: up ? "Volume-confirmed breakout" : "Trigger level reached",
            });
          }
        });
      }
      (data.alerts || []).forEach((a) => seen.current.add(a.id));
      firstLoad.current = false;
      setAlerts(data.alerts || []);
      setUnread(data.unread || 0);
    } catch { /* noop */ }
  };

  useEffect(() => {
    poll();
    const id = setInterval(poll, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await client.post("/alerts/read").catch(() => {});
      setUnread(0);
      setAlerts((a) => a.map((x) => ({ ...x, read: true })));
    }
  };

  return (
    <div className="relative">
      <button data-testid="alerts-bell" onClick={toggle}
        className="relative flex items-center rounded-sm border border-white/10 p-1.5 text-gray-400 transition-colors duration-200 hover:border-white/20 hover:text-white">
        <Bell size={15} />
        {unread > 0 && (
          <span data-testid="alerts-unread" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF3B30] px-1 font-mono-sg text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-50 w-80 border border-white/10 bg-[#111111] shadow-xl" data-testid="alerts-dropdown">
          <div className="border-b border-white/10 px-3 py-2 font-head text-sm font-bold">State Alerts</div>
          <div className="max-h-96 overflow-auto">
            {alerts.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-gray-600">No transitions yet. Add symbols to your watchlist — you'll be pinged when they move to TRIGGER or CONFIRMED.</p>
            ) : (
              alerts.map((a) => {
                const Icon = a.to_state === "CONFIRMED" ? Flame : Zap;
                const color = a.to_state === "CONFIRMED" ? "#00FF66" : "#3B9EFF";
                return (
                  <div key={a.id} className="flex items-center gap-2.5 border-b border-white/5 px-3 py-2.5">
                    <Icon size={15} style={{ color }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-sg text-sm">
                        <span className="font-bold text-white">{a.ticker}</span>
                        <span className="text-gray-500"> {a.from_state} → </span>
                        <span style={{ color }} className="font-bold">{LABEL[a.to_state]}</span>
                      </div>
                      <div className="font-mono-sg text-[10px] text-gray-600">{new Date(a.ts).toLocaleString()}</div>
                    </div>
                    {!a.read && <span className="h-2 w-2 rounded-full bg-[#FF3B30]" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
